/**
 * Biometric login using the OS keychain (Face ID / Touch ID / fingerprint).
 *
 * Flow:
 *  1. After a successful password / OAuth login, `enableBiometricLogin()`
 *     stores the current session token in the OS keychain, gated behind a
 *     biometric prompt.
 *  2. On next app launch, if biometrics is enabled, `unlockWithBiometric()`
 *     prompts Face ID / fingerprint and returns the stored token to hand
 *     to the AppContext login flow.
 *
 * Web / non-native builds always return `{ok:false}` so callers fall back
 * to the normal password screen.
 */

import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { isNative } from './platform';

const KEYCHAIN_SERVER = 'com.connectify.esim.session';
const OPT_IN_FLAG = 'biometric_opt_in';

export async function isBiometricAvailable() {
  if (!isNative()) return { available: false, reason: 'not-native' };
  try {
    const r = await NativeBiometric.isAvailable({ useFallback: false });
    return {
      available: !!r.isAvailable,
      type: r.biometryType,
      face: r.biometryType === BiometryType.FACE_ID || r.biometryType === BiometryType.FACE_AUTHENTICATION,
      fingerprint: r.biometryType === BiometryType.TOUCH_ID || r.biometryType === BiometryType.FINGERPRINT,
      reason: r.errorCode,
    };
  } catch (e) {
    return { available: false, reason: String(e) };
  }
}

export async function isBiometricEnabled() {
  const { value } = await Preferences.get({ key: OPT_IN_FLAG });
  return value === '1';
}

/**
 * Persist the session token in the OS keychain, gated behind biometric.
 * Call this after the user opts in from Settings.
 */
export async function enableBiometricLogin({ email, sessionToken }) {
  if (!isNative()) return { ok: false, reason: 'not-native' };
  if (!email || !sessionToken) return { ok: false, reason: 'missing-input' };

  try {
    await NativeBiometric.setCredentials({
      username: email,
      password: sessionToken,
      server: KEYCHAIN_SERVER,
    });
    await Preferences.set({ key: OPT_IN_FLAG, value: '1' });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: String(e) };
  }
}

export async function disableBiometricLogin() {
  if (!isNative()) return;
  try {
    await NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVER });
  } catch (_) {
    // ignore — nothing stored
  }
  await Preferences.set({ key: OPT_IN_FLAG, value: '0' });
}

/**
 * Prompt for biometrics and, on success, return `{email, sessionToken}` so
 * the caller can restore the session without re-entering a password.
 */
export async function unlockWithBiometric() {
  if (!isNative()) return { ok: false, reason: 'not-native' };
  const enabled = await isBiometricEnabled();
  if (!enabled) return { ok: false, reason: 'not-enabled' };

  try {
    // OS-native prompt — fails hard on cancel / no-match; there is no
    // silent verification path (this is the correct behaviour).
    await NativeBiometric.verifyIdentity({
      reason: 'Unlock Connectify',
      title: 'Connectify',
      subtitle: 'Sign in with biometrics',
      description: 'Use Face ID or Touch ID to open your account',
    });

    const creds = await NativeBiometric.getCredentials({ server: KEYCHAIN_SERVER });
    if (!creds?.username || !creds?.password) return { ok: false, reason: 'no-creds' };
    return { ok: true, email: creds.username, sessionToken: creds.password };
  } catch (e) {
    return { ok: false, reason: String(e) };
  }
}
